
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import MenuBar from '@/components/MenuBar';
import InterviewCard from '@/components/interview/InterviewCard';
import { Interview } from '@/types/interview';
import { InterviewMapperService } from '@/services/interview/management/base/interviewMapperService';

const QuestionPage = () => {
  const { question } = useParams();
  const decodedQuestion = question ? decodeURIComponent(question) : '';
  
  // Fix the misspelled question if it matches the specific case
  const fixedQuestion = decodedQuestion === 'Hva slags politikk kan vi forvente fra Emmanuel Macrons første regjerningsperiode?' 
    ? 'Hva slags politikk kan vi forvente fra Emmanuel Macrons første regjeringsperiode?'
    : decodedQuestion;

  const { data: interviews, isLoading } = useQuery({
    queryKey: ['interviews', 'question', fixedQuestion],
    queryFn: async () => {
      // Try to get interviews with the corrected spelling first
      let { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('question', fixedQuestion);
        
      // If no results and we're using the fixed version, try the original spelling
      if ((!data || data.length === 0) && fixedQuestion !== decodedQuestion) {
        const fallbackResult = await supabase
          .from('interviews')
          .select('*')
          .eq('question', decodedQuestion);
          
        data = fallbackResult.data;
        error = fallbackResult.error;
      }

      if (error) throw error;

      console.log('Retrieved interview data:', data);

      // Transform the data to match the Interview type using the mapper service
      return (data || []).map(interview => {
        // Make a copy of the interview to avoid modifying the original data
        const mappedInterview = InterviewMapperService.mapDatabaseInterviewToModel(interview);
        
        // Process aiSummary - ensure it's always an array
        if (typeof mappedInterview.aiSummary === 'string' && mappedInterview.aiSummary) {
          // If the aiSummary is a string containing line breaks, convert to array
          if (mappedInterview.aiSummary.includes('\n') || 
              mappedInterview.aiSummary.includes('•') ||
              mappedInterview.aiSummary.includes('-')) {
            
            // Split by line breaks or bullet points
            const points = mappedInterview.aiSummary
              .split(/\n|•|-/)
              .map(point => point.trim())
              .filter(Boolean);
              
            console.log('Converted aiSummary from string to array:', points);
            mappedInterview.aiSummary = points;
          } else if (mappedInterview.aiSummary.trim().length > 0) {
            // If it's a simple string with no line breaks or bullets, make it an array with one item
            mappedInterview.aiSummary = [mappedInterview.aiSummary.trim()];
          } else {
            // Empty string case
            mappedInterview.aiSummary = [];
          }
        } else if (!mappedInterview.aiSummary) {
          // Ensure we always have an array even if empty
          mappedInterview.aiSummary = [];
        } else if (Array.isArray(mappedInterview.aiSummary)) {
          // Already an array, just make sure all items are strings
          mappedInterview.aiSummary = mappedInterview.aiSummary
            .filter(item => item !== null && item !== undefined)
            .map(item => String(item));
        }
        
        // Override the question if needed to fix spelling
        if (interview.question === decodedQuestion) {
          mappedInterview.question = fixedQuestion;
        }
        
        return mappedInterview;
      });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MenuBar />
        <div className="max-w-4xl mx-auto p-4">
          <div className="animate-pulse">
            <div className="h-64 bg-gray-200 rounded-lg mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MenuBar />
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-semibold mb-6">
          Intervjuer som diskuterer: {fixedQuestion}
        </h1>
        <div className="space-y-12">
          {interviews?.map((interview, index) => (
            <InterviewCard 
              key={interview.id} 
              {...interview}
              isReversed={index % 2 !== 0}
              articleNumber={index + 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuestionPage;
